// Menu de navigation global (bouton ☰), présent dans l'en-tête de chaque écran.
// Se construit à partir du registre SECTIONS_MENU : ajouter une section là-bas la
// fait apparaître ici automatiquement. La section courante est mise en évidence.
// « Se déconnecter » est en pied de menu (action, pas une section).

import { useEffect, useRef, useState } from 'react'
import { SECTIONS_MENU, useNav, type SectionId } from '@/lib/nav'
import { useAuth } from '@/lib/auth'

export default function MenuNavigation() {
  const { sectionCourante, naviguer } = useNav()
  const { user, logout } = useAuth()
  const [ouvert, setOuvert] = useState(false)
  const conteneurRef = useRef<HTMLDivElement>(null)

  // Ferme au clic à l'extérieur et à la touche Échap (uniquement quand ouvert).
  useEffect(() => {
    if (!ouvert) return
    const surClic = (e: MouseEvent) => {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target as Node)) {
        setOuvert(false)
      }
    }
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false)
    }
    document.addEventListener('mousedown', surClic)
    document.addEventListener('keydown', surTouche)
    return () => {
      document.removeEventListener('mousedown', surClic)
      document.removeEventListener('keydown', surTouche)
    }
  }, [ouvert])

  const allerVers = (section: SectionId) => {
    setOuvert(false)
    naviguer(section)
  }

  // Les entrées adminOnly ne sont visibles que pour l'admin.
  const sections = SECTIONS_MENU.filter((s) => !s.adminOnly || user?.role === 'admin')

  return (
    <div ref={conteneurRef} className="relative">
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label="Menu de navigation"
        title="Menu"
        className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-lg leading-none text-bdspInk transition hover:bg-stone-50"
      >
        ☰
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-xl"
        >
          {sections.map((s) => {
            const active = s.id === sectionCourante
            return (
              <button
                key={s.id}
                type="button"
                role="menuitem"
                onClick={() => allerVers(s.id)}
                aria-current={active ? 'page' : undefined}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                  active
                    ? 'bg-bdspIndigoSoft font-semibold text-bdspInk'
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                <span aria-hidden="true" className="text-base">
                  {s.emoji}
                </span>
                <span className="flex-1">{s.label}</span>
                {active && (
                  <span aria-hidden="true" className="text-bdspIndigo">
                    ✓
                  </span>
                )}
              </button>
            )
          })}

          <div className="my-1 border-t border-stone-200" />

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOuvert(false)
              void logout()
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
          >
            <span aria-hidden="true" className="text-base">
              🚪
            </span>
            <span className="flex-1">Se déconnecter</span>
          </button>
        </div>
      )}
    </div>
  )
}
